import {
  ATTACHMENT_LIMITS,
  type Attachment,
  type AttachmentList,
  type AttachmentsPart,
  type ClassificationLevel,
  type DocumentOrigin,
  openModeOf,
  uploadLimitMbFor,
} from "@esi/shared";
import { useQueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import {
  BookOpenText,
  CircleCheck,
  CircleDashed,
  Download,
  ExternalLink,
  Globe,
  Paperclip,
  Pause,
  RotateCcw,
  Trash2,
  TriangleAlert,
  X,
} from "lucide-react";
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { NativeSelect } from "@/components/NativeSelect";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { formatBytes } from "../../i18n/format.ts";
import { failureText } from "../../projects/FileRow.tsx";
import { useSourceViewer } from "../../projects/SourceViewer.tsx";
import { ACCEPTED_FILES, fileMediaType } from "../../projects/upload.ts";
import { useSession } from "../../session.tsx";
import { attachmentApi, attachmentDownloadHref, attachmentOpenHref } from "./api.ts";
import {
  type AttachmentDraft,
  accessDenied,
  attachmentActionError,
  canRetryAttachment,
  type DraftState,
  type LocalFile,
} from "./controller.ts";
import { mediaIcon, mediaKind } from "./media.ts";
import { clearAttachmentAccess, invalidateAttachments } from "./queries.ts";
import { type ReferenceUi, useReferenceFlag } from "./reference-ui.ts";

export const AttachmentContext = createContext<{
  conversationId: string;
  list: AttachmentList | undefined;
  /** The list failed for a reason other than an access refusal, with nothing to show. */
  error?: unknown;
  refetch?: () => void;
  /** Sent messages' reference view state, kept across a message remounting (#231). */
  ui?: ReferenceUi;
} | null>(null);
export const useAttachmentContext = () => useContext(AttachmentContext);

export function AttachmentPicker(props: {
  onFiles: (files: File[]) => void;
  onOpen?: () => void;
  disabled?: boolean;
  buttonRef?: React.RefObject<HTMLButtonElement | null>;
}) {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        ref={props.buttonRef}
        aria-label={t("attachments.attach")}
        disabled={props.disabled}
        onClick={() => (props.onOpen ? props.onOpen() : input.current?.click())}
      >
        <Paperclip aria-hidden="true" strokeWidth={1.75} />
      </Button>
      <input
        ref={input}
        type="file"
        multiple
        accept={ACCEPTED_FILES}
        className="sr-only"
        tabIndex={-1}
        aria-label={t("attachments.choose")}
        onChange={(e) => {
          const files = [...(e.currentTarget.files ?? [])];
          e.currentTarget.value = "";
          if (files.length) {
            props.onFiles(files);
          }
        }}
      />
    </>
  );
}

/** Only this ID-addressed API redirect is rendered, never the short-lived storage URL. */
export function AttachmentDownload(props: {
  conversationId: string;
  attachment: Attachment;
  children?: ReactNode;
  menu?: boolean;
}) {
  const { t } = useTranslation();
  const a = props.attachment;
  if (a.status !== "ready" || !a.downloadAllowed) {
    return null;
  }
  const link = (
    <a
      href={attachmentDownloadHref(props.conversationId, a.id)}
      target="_blank"
      rel="noopener noreferrer"
      referrerPolicy="no-referrer"
      aria-label={t("attachments.download", { name: a.title })}
      className="inline-flex items-center gap-1 rounded-md p-2 text-xs text-accent-text hover:bg-surface-hover"
    >
      <Download aria-hidden="true" className="size-3.5" />
      {props.children}
    </a>
  );
  return props.menu ? <DropdownMenuItem asChild>{link}</DropdownMenuItem> : link;
}

/**
 * Open (openspec change `open-in-browser`): a ready PDF or image opens in a new tab through the
 * ID-addressed open redirect, a text, Markdown or SVG file in the in-app viewer. Nothing is requested
 * before the user activates it; other types have no Open (Download only).
 */
export function AttachmentOpen(props: {
  conversationId: string;
  attachment: Attachment;
  children?: ReactNode;
  menu?: boolean;
  /** Where focus goes when the viewer closes (the row of a menu that is gone by then). */
  returnFocus?: () => HTMLElement | null;
}) {
  const { t } = useTranslation();
  const view = useSourceViewer();
  const a = props.attachment;
  const mode = a.status === "ready" && a.downloadAllowed ? openModeOf(a.mediaType) : null;
  if (!mode) {
    return null;
  }
  const className =
    "inline-flex items-center gap-1 rounded-md p-2 text-xs text-accent-text hover:bg-surface-hover";
  if (mode === "tab") {
    const link = (
      <a
        href={attachmentOpenHref(props.conversationId, a.id)}
        target="_blank"
        rel="noopener noreferrer"
        referrerPolicy="no-referrer"
        aria-label={`${t("attachments.open", { name: a.title })} ${t("app.newTab")}`}
        className={className}
      >
        <ExternalLink aria-hidden="true" className="size-3.5" />
        {props.children}
      </a>
    );
    return props.menu ? <DropdownMenuItem asChild>{link}</DropdownMenuItem> : link;
  }
  const show = () =>
    view({
      kind: "attachment",
      conversationId: props.conversationId,
      attachmentId: a.id,
      title: a.title,
      ...(props.returnFocus && { returnFocus: props.returnFocus }),
    });
  return props.menu ? (
    // After the menu releases its focus trap, like every overlay opened from a menu.
    <DropdownMenuItem onSelect={() => setTimeout(show, 0)}>
      <BookOpenText aria-hidden="true" />
      {props.children}
    </DropdownMenuItem>
  ) : (
    <button
      type="button"
      onClick={show}
      aria-label={t("attachments.open", { name: a.title })}
      className={className}
    >
      <BookOpenText aria-hidden="true" className="size-3.5" />
      {props.children}
    </button>
  );
}

/** Reusable creator-only consequence action. No optimistic deletion or absent=tombstone guess. */
export function AttachmentDelete(props: {
  conversationId: string;
  attachment: Attachment;
  canManage: boolean;
  onDeleted?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
  returnFocus?: () => HTMLElement | null;
}) {
  const { t } = useTranslation();
  const { csrfToken } = useSession();
  const client = useQueryClient();
  const [internalOpen, setInternalOpen] = useState(false);
  const open = props.open ?? internalOpen;
  const setOpen = props.onOpenChange ?? setInternalOpen;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const trigger = useRef<HTMLButtonElement>(null);
  if (!props.canManage) {
    return null;
  }
  return (
    <>
      {props.hideTrigger ? null : (
        <Button
          ref={trigger}
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={`${t("attachments.delete")}: ${props.attachment.title}`}
          onClick={() => setOpen(true)}
        >
          <Trash2 aria-hidden="true" />
        </Button>
      )}
      <AppDialog
        open={open}
        onClose={() => {
          if (!busy) {
            setOpen(false);
          }
        }}
        title={t("attachments.deleteTitle")}
        description={t("attachments.deleteBody")}
        returnFocus={props.returnFocus ?? trigger.current}
      >
        {error ? <Notice tone="danger">{error}</Notice> : null}
        <DialogFooter sticky>
          <Button type="button" variant="outline" disabled={busy} onClick={() => setOpen(false)}>
            {t("attachments.cancel")}
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              setError(undefined);
              void attachmentApi(props.conversationId, csrfToken)
                .delete(props.attachment.id)
                .then(() => {
                  props.onDeleted?.();
                  invalidateAttachments(client, props.conversationId);
                  setOpen(false);
                })
                .catch((e) => {
                  if (accessDenied(e)) {
                    clearAttachmentAccess(client, props.conversationId);
                    invalidateAttachments(client, props.conversationId);
                  }
                  setError(attachmentActionError(e));
                })
                .finally(() => setBusy(false));
            }}
          >
            {t("attachments.delete")}
          </Button>
        </DialogFooter>
      </AppDialog>
    </>
  );
}

/**
 * Who may read a file of `level`, from its readers floor (standalone-attachments-private D4): a
 * `named` floor reads as the uploader and the people they name, whatever the level is called,
 * and true for each of them; the level's no-download note follows when asked. Empty for an
 * unknown level.
 */
export function floorWording(
  t: TFunction,
  level: ClassificationLevel | undefined,
  options: { downloadNote?: boolean } = {},
): string {
  if (!level) {
    return "";
  }
  return [
    level.readersFloor === "entity"
      ? t("attachments.floorEntity")
      : level.readersFloor === "named"
        ? t("attachments.floorNamed")
        : t("attachments.floorProject"),
    options.downloadNote && !level.downloadAllowed ? t("upload.levelNoDownload") : null,
  ]
    .filter(Boolean)
    .join(" ");
}

/**
 * The "From outside the company" toggle of a standalone file (D6): origin stays editable until a
 * message first uses the file. Before the upload intent it relabels the staged file; afterwards
 * it asks the server. Absent once sent, for anyone but the creator, and in project
 * conversations (their origin is set in the review).
 */
export function OriginToggle(props: {
  draft: AttachmentDraft;
  list: AttachmentList | undefined;
  local?: LocalFile | undefined;
  attachment?: Attachment | undefined;
  title: string;
  /** `chip`: an icon in the chip; `details`: a labelled button in the details popover. */
  variant: "chip" | "details";
}) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const { csrfToken } = useSession();
  const [pending, setPending] = useState<DocumentOrigin>();
  const [error, setError] = useState<string>();
  const { draft, list, local: f, attachment: a } = props;
  const composer = list?.composer;
  const id = a?.id ?? f?.attachmentId;
  const sent = id ? composer?.attachmentStates.find((s) => s.attachmentId === id)?.sent : false;
  // A server record known to the list, unsent; or a staged file before its intent.
  const editable = id ? a !== undefined && sent === false : f?.phase === "review";
  if (!composer?.privateOnly || !composer.canManage || !editable || a?.status === "failed") {
    return null;
  }
  const origin = pending ?? a?.origin ?? f?.origin ?? "internal";
  const external = origin === "external";
  const toggle = () => {
    const next: DocumentOrigin = external ? "internal" : "external";
    setError(undefined);
    if (!id && f) {
      draft.labels(f.key, f.classification ?? composer.uploadPolicy.defaultClassification, next);
      return;
    }
    if (!id) {
      return;
    }
    setPending(next);
    void attachmentApi(draft.conversationId, csrfToken)
      .origin(id, next)
      .then((updated) =>
        draft.originChanged(id, updated.origin === "external" ? "external" : "internal"),
      )
      .catch((e) => {
        if (accessDenied(e)) {
          clearAttachmentAccess(client, draft.conversationId);
        }
        setError(attachmentActionError(e));
      })
      .finally(() => {
        invalidateAttachments(client, draft.conversationId);
        setPending(undefined);
      });
  };
  if (props.variant === "chip") {
    return (
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        aria-pressed={external}
        aria-label={`${t("attachments.fromOutside")}: ${props.title}`}
        title={error ?? t("attachments.fromOutside")}
        disabled={pending !== undefined}
        className="text-fg-muted aria-pressed:bg-surface-hover aria-pressed:text-accent-text"
        onClick={toggle}
      >
        <Globe aria-hidden="true" />
      </Button>
    );
  }
  return (
    <span className="flex max-w-full min-w-0 flex-col items-start gap-1">
      {/* Wraps instead of overflowing the popover: the French label is long at 390 px. */}
      <Button
        type="button"
        variant="outline"
        size="xs"
        aria-pressed={external}
        disabled={pending !== undefined}
        className="h-auto min-h-6 max-w-full justify-start py-1 text-left whitespace-normal aria-pressed:border-accent-text aria-pressed:text-accent-text"
        onClick={toggle}
      >
        <Globe aria-hidden="true" />
        {t("attachments.fromOutside")}
      </Button>
      {error ? <span className="text-xs text-danger-fg">{error}</span> : null}
    </span>
  );
}

export function AttachmentReview(props: {
  conversationId: string;
  list: AttachmentList | undefined;
  draft: AttachmentDraft;
  state: DraftState;
  returnFocus: () => HTMLElement | null;
  /** Opened by itself for a batch of new files: their number, said first. */
  lead?: number | null | undefined;
}) {
  const { t } = useTranslation();
  const { list, state, draft } = props;
  const policy = list?.composer?.uploadPolicy;
  const picker = useRef<HTMLInputElement>(null);
  const lead = useRef<HTMLParagraphElement>(null);
  const local = state.files.filter((f) => f.phase === "review");
  const count = state.selected.length + state.files.filter((f) => !f.attachmentId).length;
  const valid = list?.composer?.canUpload && count <= ATTACHMENT_LIMITS.perMessage;
  // A standalone conversation: files upload private without a review; this is Manage only.
  const privateOnly = list?.composer?.privateOnly === true;
  return (
    <AppDialog
      open={state.review}
      onClose={() => draft.closeReview()}
      title={t("attachments.title")}
      description={t("attachments.scope")}
      returnFocus={props.returnFocus}
      // Opened by itself for new files: start on the lead, not on a control, so keystrokes
      // meant for the question change nothing; Tab reaches Choose files, then the first classification.
      initialFocus={() => (props.lead ? lead.current : null)}
    >
      {props.lead ? (
        <p ref={lead} tabIndex={-1} className="text-sm font-medium text-fg outline-none">
          {t("attachments.reviewLead", { count: props.lead })}
        </p>
      ) : null}
      {/* Only once the list has answered without it: never while it is still loading. */}
      {list && !list.composer ? <Notice>{t("attachments.metadata")}</Notice> : null}
      {list?.composer?.reason ? <Notice>{t(`attachments.${list.composer.reason}`)}</Notice> : null}
      {policy ? (
        <p className="text-xs text-fg-muted">
          {t("attachments.max", {
            mb: policy.maxUploadMb,
            imageMb: uploadLimitMbFor("image/png", policy.maxUploadMb),
          })}
        </p>
      ) : null}
      {privateOnly ? null : (
        <Button
          type="button"
          variant="outline"
          className="self-start"
          disabled={!list?.composer?.canUpload}
          onClick={() => picker.current?.click()}
        >
          <Paperclip aria-hidden="true" />
          {t("attachments.choose")}
        </Button>
      )}
      {privateOnly ? null : (
        <input
          ref={picker}
          className="sr-only"
          tabIndex={-1}
          type="file"
          multiple
          accept={ACCEPTED_FILES}
          aria-label={t("attachments.choose")}
          onChange={(e) => {
            draft.add([...(e.currentTarget.files ?? [])]);
            e.currentTarget.value = "";
          }}
        />
      )}
      {count > ATTACHMENT_LIMITS.perMessage ? (
        <Notice tone="warning">{t("attachments.limit")}</Notice>
      ) : null}
      <div className="min-w-0 divide-y divide-line">
        {local.map((f) => {
          const classification = f.classification ?? policy?.defaultClassification ?? "";
          const level = policy?.classifications.find((l) => l.id === classification);
          const help = floorWording(
            t,
            level ?? {
              id: classification,
              label: classification,
              downloadAllowed: true,
              readersFloor: "project",
            },
            { downloadNote: true },
          );
          const Icon = mediaIcon(f.file ? fileMediaType(f.file) : null);
          return (
            <div key={f.key} className="flex min-w-0 flex-col gap-2 py-3">
              <div className="flex min-w-0 items-center gap-2">
                <Icon aria-hidden="true" className="size-4 shrink-0 text-fg-muted" />
                <span className="min-w-0 flex-1 break-all text-sm font-medium">{f.title}</span>
                <span className="shrink-0 text-xs text-fg-muted tabular-nums">
                  {formatBytes(f.sizeBytes)}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t("attachments.remove", { name: f.title })}
                  onClick={() => draft.remove(f.key)}
                >
                  <X aria-hidden="true" />
                </Button>
              </div>
              {privateOnly ? (
                <span className="text-xs text-fg-muted">
                  {level?.label ?? classification} ·{" "}
                  {f.origin === "external"
                    ? t("upload.originLabel.external")
                    : t("upload.originLabel.internal")}
                </span>
              ) : (
                <div className="grid min-w-0 grid-cols-2 gap-2">
                  <label
                    htmlFor={`${f.key}-classification`}
                    className="flex min-w-0 flex-col gap-1 text-xs text-fg-secondary"
                  >
                    {t("upload.classification")}
                    <NativeSelect
                      id={`${f.key}-classification`}
                      value={classification}
                      disabled={!policy}
                      onChange={(e) => draft.labels(f.key, e.target.value, f.origin)}
                    >
                      {policy?.classifications.map((l) => (
                        <option key={l.id} value={l.id}>
                          {l.label}
                        </option>
                      ))}
                    </NativeSelect>
                  </label>
                  <label
                    htmlFor={`${f.key}-origin`}
                    className="flex min-w-0 flex-col gap-1 text-xs text-fg-secondary"
                  >
                    {t("upload.origin")}
                    <NativeSelect
                      id={`${f.key}-origin`}
                      value={f.origin}
                      onChange={(e) =>
                        draft.labels(
                          f.key,
                          classification,
                          e.target.value === "external" ? "external" : "internal",
                        )
                      }
                    >
                      <option value="internal">{t("upload.originLabel.internal")}</option>
                      <option value="external">{t("upload.originLabel.external")}</option>
                    </NativeSelect>
                  </label>
                </div>
              )}
              <p className="text-xs text-fg-muted">{help}</p>
            </div>
          );
        })}
        {list?.attachments.map((a) => {
          const sent = list.composer?.attachmentStates.find((s) => s.attachmentId === a.id)?.sent;
          // Upload permission, as the server's turn refusal: managing alone cannot add a file.
          const selectable = list.composer?.canUpload && a.status === "ready" && sent === false;
          const level = policy?.classifications.find((l) => l.id === a.classification);
          return (
            <div key={a.id} className="flex min-w-0 items-start gap-2 py-3">
              <label className="flex min-w-0 flex-1 items-start gap-2">
                <input
                  type="checkbox"
                  className="mt-1 size-4 shrink-0 accent-(--action-solid)"
                  disabled={!selectable}
                  checked={state.selected.includes(a.id)}
                  onChange={(e) => draft.select(a.id, e.target.checked)}
                />
                <span className="flex min-w-0 flex-col gap-1">
                  <span className="break-all text-sm font-medium">{a.title}</span>
                  <span className="text-xs text-fg-muted">
                    {level?.label ?? a.classification} ·{" "}
                    {a.origin === "external"
                      ? t("upload.originLabel.external")
                      : t("upload.originLabel.internal")}
                  </span>
                  {level ? (
                    <span className="text-xs text-fg-muted">
                      {floorWording(t, level, { downloadNote: true })}
                    </span>
                  ) : null}
                  <AttachmentStatus attachment={a} />
                  <span className="text-xs text-fg-muted">
                    {sent === true
                      ? t("attachments.sent")
                      : sent === false
                        ? t("attachments.notSent")
                        : ""}
                  </span>
                  {a.status === "uploading" ? (
                    <span className="text-xs text-fg-secondary">{t("attachments.reselect")}</span>
                  ) : null}
                </span>
              </label>
              <div className="flex shrink-0 flex-wrap items-center">
                <AttachmentDownload conversationId={props.conversationId} attachment={a} />
                <AttachmentDelete
                  conversationId={props.conversationId}
                  attachment={a}
                  canManage={list.composer?.canManage === true}
                  onDeleted={() => {
                    // Removing the local entry deselects it too.
                    const f = state.files.find((f) => f.attachmentId === a.id);
                    if (f) {
                      draft.remove(f.key);
                    } else {
                      draft.select(a.id, false);
                    }
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
      {!local.length && !list?.attachments.length ? (
        <p className="text-sm text-fg-secondary">
          {privateOnly ? t("attachments.filesEmpty") : t("attachments.empty")}
        </p>
      ) : null}
      <DialogFooter sticky>
        <Button type="button" variant="outline" onClick={() => draft.closeReview()}>
          {privateOnly ? t("attachments.done") : t("attachments.cancel")}
        </Button>
        {privateOnly ? null : (
          <Button
            type="button"
            disabled={!valid}
            onClick={() => draft.attach(list as AttachmentList)}
          >
            {t("attachments.confirm")}
          </Button>
        )}
      </DialogFooter>
    </AppDialog>
  );
}

function attachmentStatus(a?: Attachment, local?: LocalFile, preparing = false) {
  // A listed "uploading" is stale once this browser knows better: its own failure or its check
  // wins. Every other listed status is server truth after confirmation and wins over local.
  const status =
    a?.status === "uploading" && (local?.phase === "failed" || local?.phase === "checking")
      ? local.phase
      : (a?.status ?? local?.phase ?? "checking");
  const paused = a?.failureReason === "spend_limit";
  const key = paused
    ? "paused"
    : status === "failed"
      ? "processFailed"
      : status === "uploading" || status === "queued"
        ? "uploading"
        : status === "processing"
          ? "processing"
          : status === "ready"
            ? "ready"
            : status === "checking"
              ? "checking"
              : // Staged before the composer metadata says whether a review is due.
                preparing
                ? "preparing"
                : "toReview";
  return { status, paused, key } as const;
}

/** Problems first, then work in flight, then staged, then ready: only ready items ever fold. */
const STATUS_RANK: Record<ReturnType<typeof attachmentStatus>["key"], number> = {
  processFailed: 0,
  paused: 1,
  checking: 2,
  uploading: 3,
  processing: 4,
  toReview: 5,
  preparing: 5,
  ready: 6,
};

export function AttachmentStatus(props: {
  attachment?: Attachment;
  local?: LocalFile;
  /** Icon only, with the word kept for assistive technology. */
  compact?: boolean;
  /** Staged files read Preparing until the composer metadata arrives. */
  preparing?: boolean;
}) {
  const { t } = useTranslation();
  const { status, paused, key } = attachmentStatus(props.attachment, props.local, props.preparing);
  const Icon = paused
    ? Pause
    : status === "failed"
      ? TriangleAlert
      : status === "ready"
        ? CircleCheck
        : CircleDashed;
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1 text-xs ${status === "failed" ? (paused ? "text-warning-fg" : "text-danger-fg") : status === "ready" ? "text-success-fg" : "text-fg-secondary"}`}
    >
      <Icon aria-hidden="true" className="size-3 shrink-0" />
      <span className={props.compact ? "sr-only" : undefined}>{t(`attachments.${key}`)}</span>
    </span>
  );
}

/** Presentation follows explicit creator metadata; a recipient's history implies nothing. */
export function attachmentRowActions(attachment: Attachment, list: AttachmentList | undefined) {
  const sent = list?.composer?.attachmentStates.find((s) => s.attachmentId === attachment.id)?.sent;
  return {
    sent,
    add: list?.composer?.canUpload === true && attachment.status === "ready" && sent === false,
    retry: list?.composer?.canManage === true && canRetryAttachment(attachment),
    delete: list?.composer?.canManage === true,
    download: attachment.status === "ready" && attachment.downloadAllowed,
    /** How it opens (new tab or the viewer), only where it downloads; null: Download only. */
    open:
      attachment.status === "ready" && attachment.downloadAllowed
        ? openModeOf(attachment.mediaType)
        : null,
  };
}

/** One compact attachment: type icon, truncated name (full in tooltip and name), status icon. */
export function AttachmentChip(props: {
  title: string;
  mediaType: string | undefined;
  attachment?: Attachment | undefined;
  local?: LocalFile | undefined;
  /** The name as a details trigger; plain text when there is nothing to detail. */
  name?: (name: ReactNode) => ReactNode;
  actions?: ReactNode;
  progress?: number | undefined;
  preparing?: boolean;
}) {
  const { t } = useTranslation();
  const Icon = mediaIcon(props.mediaType);
  // A cancelled upload that still landed is listed but never part of the message.
  const retained = props.local?.retained === true;
  const label = (
    <span className="truncate" title={props.title}>
      {props.title}
    </span>
  );
  return (
    <li
      className={`relative flex h-7 max-w-[9.5rem] min-w-0 items-center gap-1 overflow-hidden rounded-md border bg-surface-raised pr-0.5 pl-2 text-xs text-fg sm:max-w-[13rem] ${retained ? "border-dashed border-line-strong" : "border-line"}`}
      data-attachment-chip=""
    >
      <Icon aria-hidden="true" className="size-3.5 shrink-0 text-fg-muted" strokeWidth={1.75} />
      {props.name ? props.name(label) : label}
      {props.attachment || props.local ? (
        <AttachmentStatus
          attachment={props.attachment}
          local={props.local}
          preparing={props.preparing}
          compact
        />
      ) : null}
      {retained ? <span className="sr-only">{t("attachments.notSent")}</span> : null}
      {props.actions}
      {props.progress !== undefined ? (
        <progress
          className="absolute inset-x-0 bottom-0 h-0.5 w-full accent-(--action-solid)"
          value={props.progress}
          max={100}
          aria-label={props.title}
        />
      ) : null}
    </li>
  );
}

/** The class of a chip's name when it opens details: quiet, with a visible focus ring. */
export const chipNameClass =
  "flex min-w-0 items-center rounded-sm text-left font-medium outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring";

function DetailLine(props: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[6.5rem_minmax(0,1fr)] gap-2 text-xs">
      <dt className="text-fg-muted">{props.label}</dt>
      <dd className="min-w-0 text-fg-secondary">{props.children}</dd>
    </div>
  );
}

/**
 * Full details of one item from metadata the surface already holds: it never fetches. Sent
 * state shows only from creator composer metadata; recipients never receive it.
 */
export function AttachmentDetailsBody(props: {
  title: string;
  attachment?: Attachment | undefined;
  local?: LocalFile | undefined;
  list: AttachmentList | undefined;
  mediaType: string | undefined;
  actions?: ReactNode;
  /** The origin toggle of a standalone file not yet sent (D6). */
  originControl?: ReactNode;
}) {
  const { t } = useTranslation();
  const { attachment: a, local: f, list } = props;
  const policy = list?.composer?.uploadPolicy;
  const classification = a?.classification ?? f?.classification ?? policy?.defaultClassification;
  const level = policy?.classifications.find((l) => l.id === classification);
  const origin = a?.origin ?? f?.origin;
  const sent = a
    ? list?.composer?.attachmentStates.find((s) => s.attachmentId === a.id)?.sent
    : undefined;
  const size = a?.sizeBytes ?? f?.sizeBytes;
  const reason =
    a?.status === "failed" && a.failureReason && a.failureReason !== "spend_limit"
      ? `${failureText(a.failureReason, null)}${!canRetryAttachment(a) ? ` ${t("attachments.reselect")}` : ""}`
      : a?.status === "uploading" && !f?.error
        ? t("attachments.reselect")
        : f?.error;
  return (
    <div className="flex min-w-0 flex-col gap-3">
      <p className="text-sm font-medium break-all text-fg">{props.title}</p>
      <dl className="flex flex-col gap-1.5">
        {size !== undefined ? (
          <DetailLine label={t("attachments.size")}>
            <span className="tabular-nums">{formatBytes(size)}</span>
          </DetailLine>
        ) : null}
        <DetailLine label={t("attachments.type")}>
          {t(`attachments.kind.${mediaKind(props.mediaType)}`)}
        </DetailLine>
        {classification ? (
          <DetailLine label={t("upload.classification")}>
            <span className="flex flex-col gap-0.5">
              <span>{level?.label ?? classification}</span>
              {level ? <span className="text-fg-muted">{floorWording(t, level)}</span> : null}
            </span>
          </DetailLine>
        ) : null}
        {origin ? (
          <DetailLine label={t("upload.origin")}>
            <span className="flex flex-col gap-1">
              <span>
                {t(`upload.originLabel.${origin === "external" ? "external" : "internal"}`)}
              </span>
              {props.originControl}
              {list?.composer?.privateOnly && sent === true ? (
                <span className="text-fg-muted">{t("attachments.originLocked")}</span>
              ) : null}
            </span>
          </DetailLine>
        ) : null}
        <DetailLine label={t("attachments.status")}>
          <span className="flex flex-col gap-1">
            <AttachmentStatus attachment={a} local={f} />
            {reason ? <span>{reason}</span> : null}
            {sent !== undefined ? (
              <span>{t(sent ? "attachments.sent" : "attachments.notSent")}</span>
            ) : null}
          </span>
        </DetailLine>
      </dl>
      {a && !a.downloadAllowed ? (
        <p className="text-xs text-fg-muted">{t("upload.levelNoDownload")}</p>
      ) : null}
      {props.actions ? (
        <div className="flex flex-wrap items-center gap-1">{props.actions}</div>
      ) : null}
    </div>
  );
}

const detailsClass = "w-80 max-w-[calc(100vw-2rem)] p-3";

/** A chip name that opens its details in the shared popover; Escape returns focus to it. */
export function AttachmentDetailsTrigger(props: {
  label: ReactNode;
  title: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  triggerRef?: React.Ref<HTMLButtonElement>;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <Popover open={props.open} onOpenChange={props.onOpenChange}>
      <PopoverTrigger asChild>
        <button
          ref={props.triggerRef}
          type="button"
          className={chipNameClass}
          aria-haspopup="dialog"
          aria-label={props.title}
        >
          {props.label}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={detailsClass}
        aria-label={t("attachments.detailsFor", { name: props.title })}
        role="dialog"
      >
        {props.children}
      </PopoverContent>
    </Popover>
  );
}

/** Details anchored to an element the caller owns (a tree row opened from its menu). */
export function AttachmentDetailsAnchored(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  anchor: ReactNode;
  returnFocus: () => HTMLElement | null;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  // Set when the person clicks or tabs elsewhere: focus stays where they put it.
  const outside = useRef(false);
  // The menu that opened it hands focus back to the row as it closes: that is not a dismissal.
  const isRow = (target: EventTarget | null) => target !== null && target === props.returnFocus();
  return (
    <Popover open={props.open} onOpenChange={props.onOpenChange}>
      <PopoverAnchor asChild>{props.anchor}</PopoverAnchor>
      <PopoverContent
        align="start"
        className={detailsClass}
        aria-label={t("attachments.detailsFor", { name: props.title })}
        role="dialog"
        onOpenAutoFocus={() => {
          outside.current = false;
        }}
        onFocusOutside={(event) => {
          if (isRow(event.target)) {
            event.preventDefault();
          }
        }}
        onInteractOutside={(event) => {
          if (!isRow(event.target)) {
            outside.current = true;
          }
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          if (!outside.current) {
            props.returnFocus()?.focus();
          }
          outside.current = false;
        }}
      >
        {props.children}
      </PopoverContent>
    </Popover>
  );
}

/** Rows the tray holds: local files not yet represented by a selection, then selected IDs. */
export function trayItems(
  state: DraftState,
  list: AttachmentList | undefined,
  draft: AttachmentDraft,
) {
  const local = state.files.filter(
    (f) =>
      !f.attachmentId ||
      (!state.selected.includes(f.attachmentId) &&
        (f.retained ||
          draft.hasAttempt(f.key) ||
          !list?.attachments.some((a) => a.id === f.attachmentId))),
  );
  return [
    ...local.map((f) => ({
      key: f.key,
      local: f as LocalFile | undefined,
      attachment: list?.attachments.find((a) => a.id === f.attachmentId),
    })),
    ...state.selected.map((id) => ({
      key: id,
      local: state.files.find((f) => f.attachmentId === id),
      attachment: list?.attachments.find((a) => a.id === id),
    })),
  ];
}

/**
 * Chips shown before `+N`, about two lines of the composer: three per line from `sm`, two on
 * phones. Problem, in-flight and staged chips are never folded, so they may exceed it.
 */
export const TRAY_CHIPS = { wide: 5, narrow: 3 } as const;
const NARROW = "(max-width: 639.98px)";
const subscribeNarrow = (change: () => void) => {
  const query = window.matchMedia?.(NARROW);
  query?.addEventListener("change", change);
  return () => query?.removeEventListener("change", change);
};
const isNarrow = () => window.matchMedia?.(NARROW).matches === true;
/** References shown on a message's line before `+N`. */
export const MESSAGE_CHIPS = 5;

type TrayRow = ReturnType<typeof trayItems>[number] & { title: string };

function useTrayActions(draft: AttachmentDraft) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const { csrfToken } = useSession();
  return (row: TrayRow, size: "icon-sm" | "icon-xs") => {
    const { key, local: f, attachment: a, title } = row;
    const active = f && ["queued", "uploading", "checking"].includes(f.phase);
    const retry = a && canRetryAttachment(a);
    return (
      <>
        {retry || (f?.phase === "failed" && draft.hasAttempt(f.key)) || f?.phase === "checking" ? (
          <Button
            type="button"
            variant="ghost"
            size={size}
            aria-label={`${t("attachments.retry")}: ${title}`}
            onClick={() => {
              if (f && draft.hasAttempt(f.key)) {
                draft.retry(f.key);
              } else if (a) {
                void attachmentApi(draft.conversationId, csrfToken)
                  .retry(a.id)
                  .then(() => invalidateAttachments(client, draft.conversationId))
                  .catch(() => invalidateAttachments(client, draft.conversationId));
              }
            }}
          >
            <RotateCcw aria-hidden="true" />
          </Button>
        ) : null}
        <Button
          type="button"
          variant="ghost"
          size={size}
          aria-label={t(active ? "attachments.cancelUpload" : "attachments.remove", {
            name: title,
          })}
          onClick={() => {
            if (f) {
              if (active || draft.hasAttempt(f.key)) {
                void draft.cancel(f.key);
              } else {
                draft.remove(f.key);
              }
            } else {
              draft.select(key, false);
            }
          }}
        >
          <X aria-hidden="true" />
        </Button>
      </>
    );
  };
}

/** The full rows, shown when the tray is expanded: progress, reasons and actions per file. */
function AttachmentTrayRows(props: {
  rows: readonly TrayRow[];
  actions: (row: TrayRow, size: "icon-sm" | "icon-xs") => ReactNode;
  preparing?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <ul className="divide-y divide-line">
      {props.rows.map((row) => {
        const { key, local: f, attachment: a, title } = row;
        const Icon = mediaIcon(rowMediaType(row));
        return (
          <li key={key} className="flex min-w-0 items-center gap-2 py-2">
            <Icon aria-hidden="true" className="size-4 shrink-0 text-fg-muted" strokeWidth={1.75} />
            <span className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="truncate text-xs font-medium" title={title}>
                {title}
              </span>
              <AttachmentStatus attachment={a} local={f} preparing={props.preparing} />
              {f?.retained ? (
                <span className="text-xs text-fg-secondary">{t("attachments.notSent")}</span>
              ) : null}
              {f?.phase === "uploading" ? (
                <progress
                  className="h-1 w-full accent-(--action-solid)"
                  value={f.progress}
                  max={100}
                  aria-label={title}
                />
              ) : null}
              {a?.status === "failed" && a.failureReason && a.failureReason !== "spend_limit" ? (
                <span className="text-xs text-fg-secondary">
                  {failureText(a.failureReason, null)}
                  {!canRetryAttachment(a) ? ` ${t("attachments.reselect")}` : ""}
                </span>
              ) : null}
              {f?.error ? <span className="text-xs text-danger-fg">{f.error}</span> : null}
            </span>
            <div className="flex shrink-0 items-center">{props.actions(row, "icon-sm")}</div>
          </li>
        );
      })}
    </ul>
  );
}

/** A server record's type wins; a local file's comes from its bytes' declared type or name. */
function rowMediaType(row: { local?: LocalFile | undefined; attachment?: Attachment | undefined }) {
  return (
    row.attachment?.mediaType ?? (row.local?.file ? (fileMediaType(row.local.file) ?? "") : "")
  );
}

export function AttachmentTray(props: {
  list: AttachmentList | undefined;
  draft: AttachmentDraft;
  state: DraftState;
  /** Opens the batch review dialog; absent where there is none (Home, a conversation to be). */
  onManage?: ((trigger: HTMLElement) => void) | undefined;
}) {
  const { t } = useTranslation();
  const { state, list, draft } = props;
  const removeActions = useTrayActions(draft);
  // Staged files of an open conversation wait for its composer metadata: "Preparing".
  const preparing = !draft.conversationId.startsWith("new:") && !list?.composer;
  const actions = (row: TrayRow, size: "icon-sm" | "icon-xs") => (
    <>
      <OriginToggle
        draft={draft}
        list={list}
        local={row.local}
        attachment={row.attachment}
        title={row.title}
        variant="chip"
      />
      {removeActions(row, size)}
    </>
  );
  const [expanded, setExpanded] = useState(false);
  const [announce, setAnnounce] = useState("");
  const toggle = useRef<HTMLButtonElement>(null);
  const narrow = useSyncExternalStore(subscribeNarrow, isNarrow, () => false);
  const rows: TrayRow[] = trayItems(state, list, draft).map((row) => ({
    ...row,
    // A server ID without freshly authorized metadata never shows a cached/local name.
    title:
      row.attachment?.title ??
      (row.local && (!row.local.attachmentId || (row.local.file && list?.composer?.canManage))
        ? row.local.title
        : t("attachments.unavailable")),
  }));
  if (!rows.length) {
    return null;
  }
  // A retained upload stays in view but is not part of the message, so it never counts.
  const count = rows.filter(
    (row) => !row.local?.retained || state.selected.includes(row.key),
  ).length;
  const over = count > ATTACHMENT_LIMITS.perMessage;
  const ordered = rows
    .map((row, index) => ({ row, index }))
    .sort(
      (x, y) =>
        STATUS_RANK[attachmentStatus(x.row.attachment, x.row.local, preparing).key] -
          STATUS_RANK[attachmentStatus(y.row.attachment, y.row.local, preparing).key] ||
        x.index - y.index,
    )
    .map(({ row }) => row);
  const isReady = (row: TrayRow) => attachmentStatus(row.attachment, row.local).key === "ready";
  const unfolded = ordered.filter((row) => !isReady(row)).length;
  const room = Math.max(0, (narrow ? TRAY_CHIPS.narrow : TRAY_CHIPS.wide) - unfolded);
  let readySeen = 0;
  const visible = ordered.filter((row) => !isReady(row) || readySeen++ < room);
  const hidden = rows.length - visible.length;
  const setOpen = (next: boolean) => {
    setExpanded(next);
    setAnnounce(t("attachments.showing", { count: next ? rows.length : visible.length }));
  };
  return (
    <section className="flex flex-col gap-2 px-4 pt-3" aria-label={t("attachments.title")}>
      <div className="flex min-w-0 items-center gap-1">
        <span
          className={`min-w-0 flex-1 truncate text-xs font-medium tabular-nums ${over ? "text-warning-fg" : "text-fg-secondary"}`}
        >
          {over
            ? t("attachments.overLimit", { count, max: ATTACHMENT_LIMITS.perMessage })
            : t("attachments.count", { count })}
        </span>
        {props.onManage ? (
          <Button
            type="button"
            variant="ghost"
            size="xs"
            onClick={(e) => props.onManage?.(e.currentTarget)}
          >
            {t("attachments.manage")}
          </Button>
        ) : null}
        <Button
          ref={toggle}
          type="button"
          variant="ghost"
          size="xs"
          aria-expanded={expanded}
          onClick={() => setOpen(!expanded)}
        >
          {expanded ? t("attachments.collapse") : t("attachments.showAll")}
        </Button>
      </div>
      {over ? <Notice tone="warning">{t("attachments.limit")}</Notice> : null}
      {expanded ? (
        <div className="max-h-44 overflow-y-auto">
          <AttachmentTrayRows rows={rows} actions={actions} preparing={preparing} />
        </div>
      ) : (
        <ul className="flex min-w-0 flex-wrap gap-1.5">
          {visible.map((row) => (
            <AttachmentChip
              key={row.key}
              title={row.title}
              mediaType={rowMediaType(row)}
              attachment={row.attachment}
              local={row.local}
              progress={row.local?.phase === "uploading" ? row.local.progress : undefined}
              preparing={preparing}
              name={
                row.attachment || row.local?.title === row.title
                  ? (label) => (
                      <AttachmentDetailsTrigger label={label} title={row.title}>
                        <AttachmentDetailsBody
                          title={row.title}
                          attachment={row.attachment}
                          local={row.local}
                          list={list}
                          mediaType={rowMediaType(row)}
                          originControl={
                            <OriginToggle
                              draft={draft}
                              list={list}
                              local={row.local}
                              attachment={row.attachment}
                              title={row.title}
                              variant="details"
                            />
                          }
                          actions={
                            row.attachment ? (
                              <>
                                <AttachmentOpen
                                  conversationId={draft.conversationId}
                                  attachment={row.attachment}
                                >
                                  {t("files.open")}
                                </AttachmentOpen>
                                <AttachmentDownload
                                  conversationId={draft.conversationId}
                                  attachment={row.attachment}
                                >
                                  {t("artefacts.panel.download")}
                                </AttachmentDownload>
                              </>
                            ) : null
                          }
                        />
                      </AttachmentDetailsTrigger>
                    )
                  : undefined
              }
              actions={actions(row, "icon-xs")}
            />
          ))}
          {hidden > 0 ? (
            <li>
              <Button
                type="button"
                variant="ghost"
                size="xs"
                className="h-7 tabular-nums"
                aria-label={t("attachments.moreLabel", { count: hidden })}
                onClick={() => {
                  setOpen(true);
                  // The overflow control leaves with the strip; the toggle keeps the place.
                  setTimeout(() => toggle.current?.focus(), 0);
                }}
              >
                {t("attachments.more", { count: hidden })}
              </Button>
            </li>
          ) : null}
        </ul>
      )}
      <p role="status" className="sr-only">
        {rows
          .map(
            (r) =>
              `${r.title}: ${t(`attachments.${attachmentStatus(r.attachment, r.local, preparing).key}`)}`,
          )
          .join(". ")}
      </p>
      <p aria-live="polite" className="sr-only">
        {announce}
      </p>
    </section>
  );
}

/** One sent reference: details on demand, holding Download and the creator's confirmed Delete. */
function MessageReference(props: {
  attachment: Attachment;
  conversationId: string;
  list: AttachmentList | undefined;
  /** The open details' flag in the workspace's view state. */
  uiKey: string;
  chipRef?: (el: HTMLButtonElement | null) => void;
  onDeleted?: () => void;
  /** Where focus goes when this reference is gone by the time its dialog closes. */
  fallbackFocus?: () => HTMLElement | null;
}) {
  const { t } = useTranslation();
  const { attachment: a, list } = props;
  const [open, setOpen] = useReferenceFlag(useAttachmentContext()?.ui, props.uiKey);
  const [deleting, setDeleting] = useState(false);
  const trigger = useRef<HTMLButtonElement | null>(null);
  const canManage = list?.composer?.canManage === true;
  return (
    <AttachmentChip
      title={a.title}
      mediaType={a.mediaType}
      name={(label) => (
        <>
          <AttachmentDetailsTrigger
            label={label}
            title={a.title}
            open={open}
            onOpenChange={setOpen}
            triggerRef={(el) => {
              trigger.current = el;
              props.chipRef?.(el);
            }}
          >
            <AttachmentDetailsBody
              title={a.title}
              attachment={a}
              list={list}
              mediaType={a.mediaType}
              actions={
                <>
                  <AttachmentOpen conversationId={props.conversationId} attachment={a}>
                    {t("files.open")}
                  </AttachmentOpen>
                  <AttachmentDownload conversationId={props.conversationId} attachment={a}>
                    {t("artefacts.panel.download")}
                  </AttachmentDownload>
                  {canManage ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="xs"
                      className="text-danger-fg"
                      onClick={() => {
                        setOpen(false);
                        // Let the popover release focus before the consequence dialog opens.
                        setTimeout(() => setDeleting(true), 0);
                      }}
                    >
                      <Trash2 aria-hidden="true" />
                      {t("attachments.delete")}
                    </Button>
                  ) : null}
                </>
              }
            />
          </AttachmentDetailsTrigger>
          <AttachmentDelete
            conversationId={props.conversationId}
            attachment={a}
            canManage={canManage}
            hideTrigger
            open={deleting}
            onOpenChange={setDeleting}
            returnFocus={() =>
              trigger.current?.isConnected ? trigger.current : (props.fallbackFocus?.() ?? null)
            }
            onDeleted={props.onDeleted}
          />
        </>
      )}
      attachment={a}
    />
  );
}

export function MessageAttachments({ part }: { part: AttachmentsPart }) {
  const scope = useAttachmentContext();
  const { t } = useTranslation();
  // The same references are the same message, before and after it remounts.
  const uiKey = part.data.attachments.map((ref) => ref.documentId).join(":");
  const [expanded, setExpanded] = useReferenceFlag(scope?.ui, `expanded:${uiKey}`);
  const [announce, setAnnounce] = useState("");
  const chips = useRef<(HTMLButtonElement | null)[]>([]);
  const more = useRef<HTMLButtonElement>(null);
  const collapse = useRef<HTMLButtonElement>(null);
  const group = useRef<HTMLUListElement>(null);
  // A deleted reference turns unavailable once the list refreshes, taking focus with it.
  const deleted = useRef<number | null>(null);
  const refs = part.data.attachments.map((ref) => ({
    ref,
    attachment: scope?.list?.attachments.find((a) => a.id === ref.documentId),
  }));
  const nearest = (index: number) =>
    chips.current.slice(index + 1).find((el) => el?.isConnected) ??
    chips.current
      .slice(0, index)
      .reverse()
      .find((el) => el?.isConnected) ??
    group.current;
  useEffect(() => {
    const index = deleted.current;
    if (index === null || chips.current[index]?.isConnected) {
      return;
    }
    deleted.current = null;
    if (!document.activeElement || document.activeElement === document.body) {
      nearest(index)?.focus();
    }
  });
  const shown = expanded ? refs : refs.slice(0, MESSAGE_CHIPS);
  const hidden = refs.length - shown.length;
  return (
    <div className="mt-2 whitespace-normal">
      <ul
        ref={group}
        tabIndex={-1}
        className="flex min-w-0 flex-wrap gap-1.5 outline-none"
        aria-label={t("attachments.count", { count: refs.length })}
      >
        {shown.map(({ ref, attachment: a }, index) =>
          a && scope ? (
            <MessageReference
              key={ref.documentId}
              attachment={a}
              conversationId={scope.conversationId}
              list={scope.list}
              uiKey={`open:${uiKey}:${ref.documentId}`}
              chipRef={(el) => {
                chips.current[index] = el;
              }}
              onDeleted={() => {
                deleted.current = index;
              }}
              fallbackFocus={() => nearest(index)}
            />
          ) : (
            // An unauthorized reference keeps its place in the count, never its former name.
            <AttachmentChip
              key={ref.documentId}
              title={t("attachments.unavailable")}
              mediaType={undefined}
            />
          ),
        )}
        {hidden > 0 ? (
          <li>
            <Button
              ref={more}
              type="button"
              variant="ghost"
              size="xs"
              className="h-7 tabular-nums"
              aria-label={t("attachments.moreLabel", { count: hidden })}
              onClick={() => {
                setExpanded(true);
                setAnnounce(t("attachments.showing", { count: refs.length }));
                // The control leaves; focus the first revealed reference that opens details.
                setTimeout(
                  () =>
                    (
                      chips.current.slice(MESSAGE_CHIPS).find((el) => el?.isConnected) ??
                      collapse.current
                    )?.focus(),
                  0,
                );
              }}
            >
              {t("attachments.more", { count: hidden })}
            </Button>
          </li>
        ) : expanded && refs.length > MESSAGE_CHIPS ? (
          <li>
            <Button
              ref={collapse}
              type="button"
              variant="ghost"
              size="xs"
              className="h-7"
              aria-expanded={true}
              onClick={() => {
                setExpanded(false);
                setAnnounce(t("attachments.showing", { count: MESSAGE_CHIPS }));
                setTimeout(() => more.current?.focus(), 0);
              }}
            >
              {t("attachments.collapse")}
            </Button>
          </li>
        ) : null}
      </ul>
      <p aria-live="polite" className="sr-only">
        {announce}
      </p>
    </div>
  );
}
